import { Controller, useForm } from 'react-hook-form';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Theme } from '@mui/material';
import { DialogClose, QlfButton } from '@qualifio/ui-commons';
import { makeStyles } from '@mui/styles';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTemplates, useUpdateTemplateName } from '../../hooks/use-templates';

interface RenameTemplateVersionDialogProps {
  open: boolean;
  templateId: string;
  currentName?: string;
  currentDescription?: string;
  onClose: () => void;
}

const useStyles = makeStyles<Theme>(({ spacing, palette, shape }) => ({
  title: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
    '&:after': {
      backgroundColor: palette.primary.main,
      content: "''",
      display: 'block',
      height: 3,
      margin: spacing(1, 'auto', 0),
      width: 40,
    },
  },
}));

export function RenameTemplateVersionDialog({
  open,
  templateId,
  currentName,
  currentDescription,
  onClose,
}: RenameTemplateVersionDialogProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { mutateAsync: updateTemplateName } = useUpdateTemplateName();
  const { templates } = useTemplates();

  const formSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1)
          .max(255)
          .refine(
            value => {
              // Check if another template (excluding current one) has the same name
              const trimmedValue = value?.trim();
              if (!trimmedValue) {
                return true;
              }
              const duplicate = templates.find(
                template => template.id !== templateId && template.name?.trim() === trimmedValue,
              );
              return !duplicate;
            },
            {
              message: t('custom.template.name.duplicate'),
            },
          ),
        description: z.string().trim().max(255),
      }),
    [templates, templateId, t],
  );

  type FormData = z.infer<typeof formSchema>;

  const resolver = zodResolver(formSchema);
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    mode: 'onChange',
    resolver,
    defaultValues: {
      name: currentName || '',
      description: currentDescription || '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({ name: currentName || '', description: currentDescription || '' });
    }
  }, [open, currentName, currentDescription, reset]);

  const onSubmit = async (data: FormData) => {
    await updateTemplateName({ templateId, name: data.name, description: data.description });
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogClose close={handleClose} />
      <DialogTitle className={classes.title}>
        {t('editor.template.version.name.dialog.title')}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ textAlign: 'start !important' }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                fullWidth
                label={t('editor.template.version.name.label')}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={{ mb: 2 }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && isValid) {
                    handleSubmit(onSubmit)();
                  }
                }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('editor.template.version.description.label')}
                error={!!errors.description}
                helperText={errors.description?.message}
                sx={{ mb: 2 }}
                multiline
                rows={3}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <QlfButton.Cancel onClick={handleClose} />
          <QlfButton.Save type="submit" disabled={!isValid} />
        </DialogActions>
      </form>
    </Dialog>
  );
}
